import type { ReactNode } from "react";
import type { FunRow, MainRow } from "../../../../engine/ranking";
import { displayName } from "../../../../engine/selectors";
import type { Player, PlayerId } from "../../../../engine/types";
import { useRemembered } from "./remember";

type Which = "main" | "fun";
const WHICH: readonly Which[] = ["main", "fun"];

/** One line of a board: place, name, games, and the number the board is ranked by. */
interface Line {
  id: PlayerId;
  rank: string;
  games: number;
  value: string;
  greyed: boolean;
}

function Row({ line, name, as: Tag, className }: { line: Line; name: string; as: "li" | "div"; className: string }) {
  return (
    <Tag className={className}>
      <span>{line.rank}</span>
      <span className="pn-board-name">{name}</span>
      <span>{line.games}</span>
      <span>{line.value}</span>
    </Tag>
  );
}

/**
 * The two boards behind a two-key switch. The heading row and my own row stay at the top of the
 * screen as the list scrolls, so I never hunt for myself. On the main board players short of the
 * podium minimum are greyed; the fun board lists whoever has fun points or has played a mini game.
 */
export function Boards({
  main,
  fun,
  me,
  players,
}: {
  main: MainRow[];
  fun: FunRow[];
  me: PlayerId;
  players: Record<PlayerId, Player>;
}) {
  const [which, setWhich] = useRemembered<Which>("bd.player.board", WHICH, "main");
  const name = (id: PlayerId): string => displayName(players[id], "phone");
  // Games on both boards are games played, from the main board.
  const played = new Map(main.map((r) => [r.id, r.games]));
  const gamesOf = (id: PlayerId): number => played.get(id) ?? 0;
  // everyone is level before a game: no rank until the first one, as on the Now tile
  const rankOf = (id: PlayerId, rank: number): string => (gamesOf(id) ? String(rank) : "-");

  const lines: Line[] =
    which === "main"
      ? main.map((r) => ({ id: r.id, rank: rankOf(r.id, r.rank), games: r.games, value: r.avg.toFixed(1), greyed: !r.eligible }))
      : fun
          .filter((r) => r.active)
          .map((r) => ({ id: r.id, rank: rankOf(r.id, r.rank), games: gamesOf(r.id), value: String(Math.round(r.funPoints)), greyed: false }));
  const mine = lines.find((l) => l.id === me);
  const off = which === "fun" ? fun.find((r) => r.id === me) : undefined;
  // Not on the fun board yet: my strip still says where I stand, which is nowhere so far.
  const pinned: Line | undefined =
    mine ?? (off ? { id: me, rank: "-", games: gamesOf(me), value: String(Math.round(off.funPoints)), greyed: false } : undefined);

  const key = (id: Which, label: string): ReactNode => (
    <button type="button" className={"pn-key" + (which === id ? " on" : "")} aria-pressed={which === id} onClick={() => setWhich(id)}>
      {label}
    </button>
  );

  return (
    <section className={`pn-board ${which}`}>
      <h2 className="pn-sr">{which === "main" ? "Leaderboard" : "Fun board"}</h2>
      <div className="pn-keys">
        {key("main", "Main")}
        {key("fun", "Fun")}
      </div>
      <div className="pn-board-top">
        <div className="pn-board-row pn-board-head">
          <span>#</span>
          <span className="pn-board-name">Name</span>
          <span>Games</span>
          <span>{which === "main" ? "Avg" : "Points"}</span>
        </div>
        {pinned && <Row as="div" line={pinned} name={name(me)} className="pn-board-row pn-board-me" />}
      </div>
      {lines.length > 0 ? (
        <ol key={which} className="pn-board-list">
          {lines.map((l) => (
            <Row
              key={l.id}
              as="li"
              line={l}
              name={name(l.id)}
              className={"pn-board-row" + (l.id === me ? " me" : "") + (l.greyed ? " ineligible" : "")}
            />
          ))}
        </ol>
      ) : (
        <p className="pn-empty">No fun points yet. They come from right picks and bounties.</p>
      )}
      {which === "main" && <p className="player-quiet">Greyed: not enough games yet to count for the podium.</p>}
    </section>
  );
}
